import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Simulator.css";
import {
  FiArrowUpRight,
  FiCheck,
  FiPhone,
  FiUser,
  FiX,
  FiCreditCard,
  FiAlertTriangle,
} from "react-icons/fi";
import GoogleForm from "../../../components/common/GoogleForm/GoogleForm";
import InteractiveGlobe from "../../../components/common/InteractiveGlobe/InteractiveGlobe";
import { formatPhone } from "../../../utils/formFormatters";

const modalFields = [
  {
    name: "nome",
    label: "Nome completo",
    googleName: "entry.803048395",
    icon: FiUser,
    type: "text",
    required: true,
    placeholder: "Digite seu nome completo",
    autoComplete: "name",
    minLength: 3,
    maxLength: 100,
  },
  {
    name: "telefone",
    label: "Celular / WhatsApp",
    googleName: "entry.1452367244",
    icon: FiPhone,
    type: "tel",
    inputMode: "tel",
    required: true,
    placeholder: "Digite seu celular ou WhatsApp",
    autoComplete: "tel",
    minLength: 14,
    format: formatPhone,
  },
  {
    name: "banco",
    label: "Banco atual",
    googleName: "entry.90330311",
    icon: FiCreditCard,
    type: "text",
    required: true,
    placeholder: "Digite o nome do seu banco",
    autoComplete: "organization",
    minLength: 2,
    maxLength: 100,
  },
];

const selectAll = (e) => e.target.select();

const sanitizeCurrency = (value) => {
  let normalized = value.replace(/[^\d,.]/g, "");

  const lastComma = normalized.lastIndexOf(",");
  const lastDot = normalized.lastIndexOf(".");

  let decimalSeparator = "";

  if (lastComma > lastDot) {
    decimalSeparator = ",";
  } else if (lastDot > lastComma) {
    decimalSeparator = ".";
  }

  if (decimalSeparator) {
    const separatorIndex = normalized.lastIndexOf(decimalSeparator);
    let integerPart = normalized.slice(0, separatorIndex);
    let decimalPart = normalized.slice(separatorIndex + 1);

    integerPart = integerPart.replace(/\D/g, "").slice(0, 6);
    decimalPart = decimalPart.replace(/\D/g, "").slice(0, 2);

    return `${integerPart || "0"}.${decimalPart}`;
  }

  return normalized.replace(/\D/g, "").slice(0, 6);
};

const sanitizeInteger = (value, max = 144) => {
  const digits = value.replace(/\D/g, "");

  if (!digits) {
    return "";
  }

  return String(Math.min(Number(digits), max));
};

const sanitizeTaxa = (value) => {
  let normalized = value.replace(",", ".").replace(/[^\d.]/g, "");

  const firstDot = normalized.indexOf(".");

  if (firstDot !== -1) {
    normalized =
      normalized.slice(0, firstDot + 1) +
      normalized.slice(firstDot + 1).replace(/\./g, "");
  }

  let [integerPart = "", decimalPart] = normalized.split(".");

  integerPart = integerPart.slice(0, 2);
  decimalPart = decimalPart?.slice(0, 2);

  if (integerPart === "0" && decimalPart === undefined) {
    return "0";
  }

  if (firstDot !== -1) {
    return `${integerPart || "0"}.${decimalPart || ""}`;
  }

  return integerPart;
};

const arredondar = (value) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

const calcularPrestacao = (taxaMensal, prazo, saldo) =>
  (saldo * taxaMensal) / (1 - Math.pow(1 + taxaMensal, -prazo));

const calcularTrocoLiberado = (parcela, taxaMensal, prazo, saldo) =>
  arredondar(
    (parcela * (1 - Math.pow(1 + taxaMensal, -prazo))) / taxaMensal - saldo
  );

const Simulator = () => {
  const [activeTab, setActiveTab] = useState("calculadora");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dialogRef = useRef(null);

  const [parcelaAtual, setParcelaAtual] = useState("");
  const [saldoDevedor, setSaldoDevedor] = useState("");
  const [prazo, setPrazo] = useState("");
  const [taxa, setTaxa] = useState("");

  const [result, setResult] = useState({
    novaParcela: "0,00",
    trocoLiberado: "0,00",
    economiaMensal: "0,00",
    economiaTotal: "0,00",
  });

  const prazoPresets = [144];
  const taxaPresets = [1.57, 1.72];

  const formatValue = (value) =>
    Number(value).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatTaxa = (value) => Number(value).toFixed(2).replace(".", ",");

  const calculadoraPreenchida =
    Number(parcelaAtual) > 0 &&
    Number(saldoDevedor) > 0 &&
    Number(prazo) > 0 &&
    Number(taxa) > 0;

  const calcularPortabilidade = () => {
    const parcela = Number(parcelaAtual);
    const saldo = Number(saldoDevedor);
    const prazoNumero = Number(prazo);
    const taxaNumero = Number(taxa);

    const taxaMensal = taxaNumero / 100;

    if (!parcela || !saldo || !prazoNumero || !taxaNumero || taxaMensal <= 0) {
      setResult({
        novaParcela: "0,00",
        trocoLiberado: "0,00",
        economiaMensal: "0,00",
        economiaTotal: "0,00",
      });

      return;
    }

    const novaParcela = calcularPrestacao(taxaMensal, prazoNumero, saldo);

    const trocoLiberado = calcularTrocoLiberado(
      parcela,
      taxaMensal,
      prazoNumero,
      saldo
    );

    const economiaMensal = parcela - novaParcela;
    const economiaTotal = economiaMensal * prazoNumero;

    setResult({
      novaParcela: formatValue(novaParcela),
      trocoLiberado: formatValue(Math.max(trocoLiberado, 0)),
      economiaMensal: formatValue(Math.max(economiaMensal, 0)),
      economiaTotal: formatValue(Math.max(economiaTotal, 0)),
    });
  };

  useEffect(() => {
    calcularPortabilidade();
  }, [parcelaAtual, saldoDevedor, prazo, taxa]);

  useEffect(() => {
    if (!isModalOpen) return;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen]);

  const openModal = () => {
    if (!calculadoraPreenchida) return;

    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const hiddenFields = [
    {
      name: "parcelaAtual",
      label: "Parcela atual",
      googleName: "entry.399848783",
      value: parcelaAtual ? `R$ ${formatValue(Number(parcelaAtual))}` : "",
    },
    {
      name: "saldoDevedor",
      label: "Saldo devedor",
      googleName: "entry.2138188351",
      value: saldoDevedor ? `R$ ${formatValue(Number(saldoDevedor))}` : "",
    },
    {
      name: "prazo",
      label: "Prazo",
      googleName: "entry.2111629721",
      value: prazo ? `${prazo} meses` : "",
    },
    {
      name: "taxa",
      label: "Taxa",
      googleName: "entry.896075638",
      value: taxa ? `${formatTaxa(Number(taxa))}% a.m.` : "",
    },
    {
      name: "novaParcela",
      label: "Nova parcela",
      googleName: "entry.710415347",
      value: `R$ ${result.novaParcela}`,
    },
    {
      name: "economiaMensal",
      label: "Economia mensal estimada",
      googleName: "entry.1092523075",
      value: `R$ ${result.economiaMensal}`,
    },
    {
      name: "economiaTotal",
      label: "Economia total no prazo",
      googleName: "entry.1171767193",
      value: `R$ ${result.economiaTotal}`,
    },
  ];

  return (
    <section id="simulador" className="section simulator">
      <div className="container">
        <div className="card simulator-card">
          <div className="simulator-tabs">
            <button
              className={`tab-item ${
                activeTab === "calculadora" ? "active" : ""
              }`}
              onClick={() => setActiveTab("calculadora")}
            >
              Calculadora
            </button>

            <button
              className={`tab-item ${activeTab === "sobre" ? "active" : ""}`}
              onClick={() => setActiveTab("sobre")}
            >
              Sobre
            </button>
          </div>

          {activeTab === "calculadora" && (
            <div className="simulator-grid">
              <div className="simulator-input">
                <div className="input-head">
                  <label>Simule sua portabilidade</label>

                  <span className="input-description">
                    Preencha os dados do seu contrato atual para visualizar
                    uma estimativa da nova parcela.
                  </span>
                </div>

                <div className="field">
                  <label htmlFor="parcela-atual">
                    Valor da parcela atual
                  </label>

                  <div className="field-input">
                    <span>R$</span>

                    <input
                      id="parcela-atual"
                      type="text"
                      inputMode="decimal"
                      value={parcelaAtual.replace(".", ",")}
                      onFocus={selectAll}
                      onChange={(e) =>
                        setParcelaAtual(sanitizeCurrency(e.target.value))
                      }
                      placeholder="Digite o valor da parcela"
                    />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="saldo-devedor">Saldo devedor</label>

                  <div className="field-input">
                    <span>R$</span>

                    <input
                      id="saldo-devedor"
                      type="text"
                      inputMode="decimal"
                      value={saldoDevedor.replace(".", ",")}
                      onFocus={selectAll}
                      onChange={(e) =>
                        setSaldoDevedor(sanitizeCurrency(e.target.value))
                      }
                      placeholder="Digite o saldo devedor"
                    />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="prazo">Prazo</label>

                  <div className="field-input">
                    <input
                      id="prazo"
                      type="text"
                      inputMode="numeric"
                      value={prazo}
                      onFocus={selectAll}
                      onChange={(e) =>
                        setPrazo(sanitizeInteger(e.target.value, 144))
                      }
                      placeholder="Digite o prazo"
                    />

                    <span>meses</span>
                  </div>

                  <div className="field-presets">
                    {prazoPresets.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`preset-btn ${
                          Number(prazo) === p ? "active" : ""
                        }`}
                        onClick={() => setPrazo(String(p))}
                      >
                        {p} meses
                      </button>
                    ))}
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="taxa">Taxa de juros ao mês</label>

                  <div className="field-input">
                    <input
                      id="taxa"
                      type="text"
                      inputMode="decimal"
                      value={taxa.replace(".", ",")}
                      onFocus={selectAll}
                      onChange={(e) => setTaxa(sanitizeTaxa(e.target.value))}
                      placeholder="Digite a taxa"
                    />

                    <span>%</span>
                  </div>

                  <div className="field-presets">
                    {taxaPresets.map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`preset-btn ${
                          Number(taxa) === t ? "active" : ""
                        }`}
                        onClick={() => setTaxa(String(t))}
                      >
                        {formatTaxa(t)}%
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="simulator-result">
                <div className="result">
                  <div className="result-main">
                    <span className="result-label">Nova parcela mensal</span>

                    <strong>R$ {result.novaParcela}</strong>
                  </div>

                  <div className="result-details">
                    <div className="result-detail">
                      <span>Parcela atual</span>

                      <strong>
                        R${" "}
                        {parcelaAtual
                          ? formatValue(Number(parcelaAtual))
                          : "0,00"}
                      </strong>
                    </div>

                    <div className="result-detail">
                      <span>Saldo devedor</span>

                      <strong>
                        R${" "}
                        {saldoDevedor
                          ? formatValue(Number(saldoDevedor))
                          : "0,00"}
                      </strong>
                    </div>

                    <div className="result-detail">
                      <span>Prazo</span>

                      <strong>{prazo ? `${prazo} meses` : "-"}</strong>
                    </div>

                    <div className="result-detail">
                      <span>Taxa aplicada</span>

                      <strong>
                        {taxa ? `${formatTaxa(Number(taxa))}% a.m.` : "—"}
                      </strong>
                    </div>
                  </div>

                  <div className="result-economy">
                    <div>
                      <span>Economia mensal</span>

                      <strong>R$ {result.economiaMensal}</strong>
                    </div>

                    <div>
                      <span>Economia total</span>

                      <strong>R$ {result.economiaTotal}</strong>
                    </div>
                  </div>

                  <div className="details">
                    <div className="details-row highlight">
                      <span>Liberação de troco</span>
                      <span>R$ {result.trocoLiberado}</span>
                    </div>

                    <div className="details-row highlight">
                      <span>Prestação</span>
                      <span>R$ {result.novaParcela}</span>
                    </div>
                  </div>

                  <div className="result-actions">
                    <button
                      type="button"
                      className={`btn btn-primary ${
                        !calculadoraPreenchida ? "disabled" : ""
                      }`}
                      onClick={openModal}
                      disabled={!calculadoraPreenchida}
                      aria-disabled={!calculadoraPreenchida}
                    >
                      <span>Tenho interesse</span>
                      <FiArrowUpRight />
                    </button>
                  </div>
                </div>
              </div>

              <div className="disclaimer">
                <div className="disclaimer-item">
                  <FiAlertTriangle />

                  <p>
                    <strong>Informações importantes:</strong> as taxas de
                    cada convênio são baseadas na tabela Selic e podem ser
                    alteradas, para mais ou para menos, de acordo com a
                    alteração da mesma.
                  </p>
                </div>

                <div className="disclaimer-item">
                  <FiAlertTriangle />

                  <p>
                    <strong>Dica para uma portabilidade bem sucedida:</strong>{" "}
                    para que seja feito um cálculo eficaz, pedimos que
                    verifique com o banco em que o contrato foi originado e
                    confira a DED (Demonstrativo da Evolução da Dívida).
                    Este documento traz o valor exato da dívida para que o
                    cálculo seja eficaz.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "sobre" && (
            <div className="about-grid">
              <div className="about-content">
                <h3>
                  Transfira seu empréstimo para outro banco com melhores condições.
                </h3>

                <p>
                  Trabalhamos com crédito consignado para beneficiários do
                  INSS e também para servidores vinculados a mais de 6.000
                  convênios em todo o Brasil.
                </p>

                <p>
                  Atuamos com os principais bancos do país, com destaque
                  para Caixa, Banco do Brasil e Santander, buscando
                  condições compatíveis com cada modalidade e convênio.
                </p>

                <ul className="about-list">
                  <li>
                    <FiCheck />
                    <span>Redução da taxa de juros mensal</span>
                  </li>

                  <li>
                    <FiCheck />
                    <span>Mesmo saldo devedor, parcela menor</span>
                  </li>

                  <li>
                    <FiCheck />
                    <span>Processo 100% digital e gratuito</span>
                  </li>

                  <li>
                    <FiCheck />
                    <span>Sem taxas antecipadas</span>
                  </li>
                </ul>
              </div>

              <div className="simulator-image">
                <InteractiveGlobe />
              </div>
            </div>
          )}
        </div>
      </div>

      {isModalOpen &&
        createPortal(
          <div
            className="simulator-modal"
            onMouseDown={handleOverlayMouseDown}
          >
            <div
              ref={dialogRef}
              className="form-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="simulator-modal-title"
              tabIndex={-1}
            >
              <button
                type="button"
                className="icon-box simulator-modal-close"
                onClick={closeModal}
                aria-label="Fechar"
              >
                <FiX />
              </button>

              <div className="input-head">
                <h3 id="simulator-modal-title">Fale com um especialista</h3>

                <span className="input-description">
                  Informe seu nome, celular e banco. Os demais dados da sua
                  simulação serão enviados automaticamente.
                </span>
              </div>

              <GoogleForm
                formUrl="https://docs.google.com/forms/d/e/1FAIpQLSfVazhfd9ynVOW1BMWbAGmO9mxxW61xQIwAuTlgn8-QMUXd5Q/formResponse"
                notifyUrl="https://formsubmit.co/ajax/fernando@egelemprestimos.com.br"
                emailSubject="Nova simulação de portabilidade"
                fields={modalFields}
                hiddenFields={hiddenFields}
                submitText="Quero minha proposta"
                privacyText="Concordo em ser contatado(a) e em compartilhar os dados da minha simulação, conforme os termos de privacidade."
                className="portabilidade"
              />
            </div>
          </div>,
          document.body
        )}
    </section>
  );
};

export default Simulator;